import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from '../libs/threejs/addons/meshopt_decoder.module.js';

export class ProductModel {
  constructor(model) {
    this.element = model;
    this.canvas = null;
    this.src = this.element.dataset.src;

    this.isDragging = false;
    this.lastMouseX = null;
    this.lastMouseY = null;

    this.rotation = {
      step: 0.00002,
      max: 0.005,
      speed: 0,
      delay: 2000,
      pause: false,
      enabled: true,
      timeout: null
    };

    const quarterRotation = Math.PI / 2;
    this.views = {
      next: 0,
      speed: 500,
      start: null,
      enabled: false,
      currentRotation: new THREE.Vector3(0, 0, 0),
      targetRotation: new THREE.Vector3(0, 0, 0),
      rotation: [
        { name: 'Front', x: 0, y: 0, z: 0 },
        { name: 'Left', x: 0, y: quarterRotation, z: 0 },
        { name: 'Top', x: quarterRotation, y: 0, z: 0 },
        { name: 'Back', x: 0, y: quarterRotation * 2, z: 0 },
        { name: 'Right', x: 0, y: quarterRotation * 3, z: 0 },
        { name: 'Bottom', x: -quarterRotation, y: quarterRotation * 4, z: 0 },
      ]
    };

    this.zoom = {
      maxDistance: 8,
      minDistance: 3.5,
      step: 1,
      speed: 250,
      enabled: false,
      start: null,
      current: 5.5,
      target: 5.5
    };

    this.fullscreenClass = 'c-product-model--fullscreen';

    this.rotateButton = this.element.querySelector('[data-js-product-model-rotate]');
    this.viewsButton = this.element.querySelector('[data-js-product-model-views]');
    this.zoomInButton = this.element.querySelector('[data-js-product-model-zoom-in]');
    this.zoomOutButton = this.element.querySelector('[data-js-product-model-zoom-out]');
    this.fullscreenButton = this.element.querySelector('[data-js-product-model-fullscreen]');

    this.dataLayer = window.dataLayer || [];

    this.init();
    this.render();
  }

  init() {
    this.setupRenderer();
    this.setupCamera();
    this.setupScene();
    this.loadModel();
    this.setupEventListeners();
  }

  setupRenderer() {
    this.canvas = document.createElement( 'div' );
    this.canvas.classList.add('c-product-model__wrapper')
    this.element.prepend( this.canvas );

    // Set the renderer
    this.renderer = new THREE.WebGLRenderer( { antialias: true } );
    this.renderer.setPixelRatio( window.devicePixelRatio );
    this.renderer.setSize( this.canvas.offsetWidth, Math.min(this.canvas.offsetWidth, window.innerHeight - 150) );
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.canvas.appendChild( this.renderer.domElement );
  }

  setupCamera() {
    // Add a new camera
    this.camera = new THREE.PerspectiveCamera( 50, this.canvas.offsetWidth / Math.min(this.canvas.offsetWidth, window.innerHeight - 150), 0.5, 20 );
    this.camera.position.set( 0, 0, this.zoom.current );

    // Add the basic orbit controls.
    this.controls = new OrbitControls( this.camera, this.renderer.domElement );
    this.controls.minDistance = this.zoom.minDistance;
    this.controls.maxDistance = this.zoom.maxDistance;
    this.controls.enablePan = false;
    this.controls.enableRotate = false;
    this.controls.update();
  }

  setupScene() {
    // Setup a basic environment
    const environment = new RoomEnvironment();
    const pmremGenerator = new THREE.PMREMGenerator( this.renderer );

    // The main scene that holds our objects
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color( 0xf2f5f7 );
    this.scene.environment = pmremGenerator.fromScene( environment ).texture;
    environment.dispose();
  }

  loadModel() {
    this.pivot = new THREE.Object3D(); // create an empty pivot object to use as the center of rotation
    this.scene.add(this.pivot);

    // Create the loader and set the compression decoder (NB: this model has no textures to decode)
    const loader = new GLTFLoader();

    // Models can be compressed for use on the web using gltfpack: https://meshoptimizer.org/gltf/
    // Example command: gltfpack -i 80BFH-2C-80_2.gltf -o 80BFH-2C-80_2.glb -cc -tc
    // The resulting model uses EXT_meshopt_compression (for geometry), which we must decode
    loader.setMeshoptDecoder( MeshoptDecoder );

    const self = this;
    loader.load( this.src, function ( gltf ) {
      const model = gltf.scene;
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());

      // Move the center of the model to the axis origin
      model.position.copy(center).multiplyScalar(-1);

      // Scale the model up/down so that it fits within the camera viewport
      const size = box.getSize(new THREE.Vector3()).length();
      const scale = 4.75 / size;
      model.scale.set(scale, scale, scale);

      // Update the position now that it has been scaled so that it again sits on the axis origin
      model.position.x = -center.x * scale;
      model.position.y = -center.y * scale;
      model.position.z = -center.z * scale;

      // Add the model to the central pivot point so that it rotates around the axis origin
      self.pivot.add(model);

      self.dataLayer.push({ event: '3dProductInteraction', eventType: 'modelLoaded' });
    });
  }

  setupEventListeners() {
    this.rotateButton.addEventListener('click', this.toggleRotation.bind(this));
    this.viewsButton.addEventListener('click', this.toggleViews.bind(this));
    this.zoomInButton.addEventListener('click', this.zoomIn.bind(this));
    this.zoomOutButton.addEventListener('click', this.zoomOut.bind(this));

    this.fullscreenButton.addEventListener('click', this.toggleFullscreen.bind(this));
    window.addEventListener('keydown', this.toggleFullscreen.bind(this));

    this.canvas.addEventListener('touchstart', this.grabModel.bind(this));
    this.canvas.addEventListener('mousedown', this.grabModel.bind(this));

    this.canvas.addEventListener('touchmove', this.dragModel.bind(this));
    this.canvas.addEventListener('mousemove', this.dragModel.bind(this));

    window.addEventListener('touchcancel', this.releaseModel.bind(this));
    window.addEventListener('touchend', this.releaseModel.bind(this));
    window.addEventListener('mouseup', this.releaseModel.bind(this));

    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  toggleRotation() {
    if (this.rotation.timeout) {
      clearTimeout(this.rotation.timeout);
    }

    if (this.rotation.speed < this.rotation.max) {
      this.rotation.enabled = true;
      this.rotation.speed = this.rotation.max;
      this.rotation.pause = false;
    } else {
      this.rotation.enabled = !this.rotation.enabled;
    }
  }

  toggleViews() {
    this.rotation.enabled = false;
    this.views.enabled = true;
    this.views.start = null;

    this.views.currentRotation = new THREE.Vector3(this.pivot.rotation.x, this.pivot.rotation.y, this.pivot.rotation.z);

    const nextRotation = this.views.rotation[this.views.next];
    this.views.targetRotation = new THREE.Vector3(nextRotation.x, nextRotation.y, nextRotation.z);

    this.views.next += 1;
    if (this.views.next >= this.views.rotation.length) {
      this.views.next = 0;
    }
  }

  zoomIn() {
    this.zoom.current = this.camera.position.z;

    this.zoom.target = this.zoom.current - this.zoom.step;
    if (this.zoom.target < this.zoom.minDistance) {
      this.zoom.target = this.zoom.minDistance;
    }

    if (this.zoom.target !== this.zoom.current) {
      this.zoom.enabled = true;
      this.zoom.start = null;
    }
  }

  zoomOut() {
    this.zoom.current = this.camera.position.z;

    this.zoom.target = this.zoom.current + this.zoom.step;
    if (this.zoom.target > this.zoom.maxDistance) {
      this.zoom.target = this.zoom.maxDistance;
    }

    if (this.zoom.target !== this.zoom.current) {
      this.zoom.enabled = true;
      this.zoom.start = null;
    }
  }

  toggleFullscreen(event) {
    if (
      (
        event.type === 'keydown' &&                                // If this was a keypress
        ( event.key === 'Escape' || event.code === 'Escape') &&    // and the key pressed was 'Escape'
        this.element.classList.contains(this.fullscreenClass)      // and we are in fullscreen view
      ) ||
      event.type !== 'keydown'  // Or if it isn't a keypress, it must be a click event
    ) {
      this.element.classList.toggle(this.fullscreenClass);

      if (this.element.classList.contains(this.fullscreenClass)) {
        document.body.style.overflow = 'hidden';
        document.body.style.visibility = 'hidden';
        this.element.style.visibility = 'visible';
      } else {
        document.body.style.overflow = '';
        document.body.style.visibility = '';
        this.element.style.visibility = '';
      }
      this.onWindowResize();
    }
  }

  grabModel(event) {
    if (event.button !== 0 && !event.touches) return;
    this.isDragging = true;
    this.pauseRotation();
  }

  dragModel(event) {
    // If we're not dragging, then we don't need to update the model position
    if (!this.isDragging) return;

    if (event.touches && event.touches.length !== 1) {
      this.releaseModel();
    }
    const mouseX = event.touches && event.touches.length ? event.touches[0].clientX : event.clientX;
    const mouseY = event.touches && event.touches.length ? event.touches[0].clientY : event.clientY;

    if (this.lastMouseX === null) {
      this.lastMouseX = mouseX;
      this.lastMouseY = mouseY;
      return;
    }

    // Find the difference between the last location of the mouse and the current location for X and Y
    const deltaX = (mouseX - this.lastMouseX) / 180;
    const deltaY = (mouseY - this.lastMouseY) / 180;

    // Update the last mouse location
    this.lastMouseX = mouseX;
    this.lastMouseY = mouseY;

    // The rotation around the Y axis is based on the mouse's X axis movement
    this.pivot.rotation.y += deltaX;

    // Set a max and min vertical angle of 90 deg so that the user cannot turn the model upside down
    const minAngle = -Math.PI / 2;
    const maxAngle = Math.PI / 2;
    const newAngle = this.pivot.rotation.x + deltaY;

    if (newAngle >= minAngle && newAngle <= maxAngle) {
      // The rotation around the X axis is based on the mouse's Y axis movement
      this.pivot.rotation.x += deltaY;
    } else {
      if (newAngle < minAngle) {
        this.pivot.rotation.x = minAngle;
      } else if (newAngle > maxAngle) {
        this.pivot.rotation.x = maxAngle;
      }
    }
  }

  releaseModel () {
    this.lastMouseX = null;
    this.lastMouseY = null;

    if (!this.isDragging)  return;

    this.isDragging = false;
    this.resumeRotation();
  }

  pauseRotation() {
    this.rotation.pause = true;
    this.rotation.speed = this.rotation.step;
    if (this.rotation.timeout) {
      clearTimeout(this.rotation.timeout);
    }
  }

  resumeRotation() {
    this.rotation.timeout = setTimeout(() => {
      this.rotation.pause = false;
    }, this.rotation.delay);
  }

  onWindowResize() {
    this.camera.aspect = this.canvas.offsetWidth / Math.min(this.canvas.offsetWidth, window.innerHeight - 150);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize( this.canvas.offsetWidth, Math.min(this.canvas.offsetWidth, window.innerHeight - 150) );
  }

  render(time) {
    this.autoRotateModel();
    this.rotateModelToView(time);
    this.normalizeRotation();
    this.zoomCamera(time);

    this.renderer.render( this.scene, this.camera );
    requestAnimationFrame(this.render.bind(this));
  }

  autoRotateModel() {
    if (!this.pivot || !this.rotation.enabled || this.rotation.pause) return;

    this.pivot.rotation.y += this.rotation.speed;

    if (this.rotation.speed <= this.rotation.max) {
      this.rotation.speed += this.rotation.step;
    }
  }

  rotateModelToView(time) {
    if (!this.pivot || !this.views.enabled) return;

    if (this.views.start === null) {
      this.views.start = time;
    }

    const elapsedTime = (time - this.views.start) / this.views.speed; // Calculate the percentage of duration since the animation started
    const progress = Math.min(elapsedTime, 1); // Calculate the progress of the animation as a value between 0 and 1
    const rotation = this.views.currentRotation.clone().lerp(this.views.targetRotation, progress); // Interpolate the rotation between the initial and target rotations using lerp

    this.pivot.rotation.set(rotation.x, rotation.y, rotation.z); // Set the rotation of the cube

    if (progress === 1) {
      // Once we've reached the new position, we can reset our timer
      this.views.enabled = false;
      this.views.start = null;
    }
  }

  normalizeRotation() {
    if (!this.pivot) return;
    // We don't want to wind up the model, so every time it completes a full rotation, let's reset it
    const fullRotation = Math.PI * 2;
    const rotation = this.pivot.rotation.y;

    // Check if the rotation value is less than 0 or greater than 2π
    if (rotation < 0 || rotation >= fullRotation) {
      const normalizedRotation = rotation % fullRotation; // Normalize rotation to a value between -2π and 2π
      const newRotation = normalizedRotation < 0 ? normalizedRotation + fullRotation : normalizedRotation; // Ensure that the rotation is positive

      this.pivot.rotation.y = newRotation;
    }
  }

  zoomCamera(time) {
    if (!this.camera || !this.zoom.enabled) return;

    if (this.zoom.start === null) {
      this.zoom.start = time;
    }

    const elapsedTime = (time - this.zoom.start) / this.zoom.speed; // Calculate the percentage of duration since the animation started
    const progress = Math.min(elapsedTime, 1); // Calculate the progress of the animation as a value between 0 and 1
    const zoom = this.zoom.current + ((this.zoom.target - this.zoom.current) * progress); // Interpolate the distance between the initial and target positions

    this.camera.position.z = zoom; // Set the new position of the camera

    if (progress === 1) {
      // Once we've reached the new position, we can reset our timer
      this.zoom.enabled = false;
      this.zoom.start = null;
    }
  }
}

const models = document.querySelectorAll('[data-js-product-model]');

if (models) {
  for (let i = 0; i < models.length; ++i) {
    new ProductModel(models[i]);
  }
}
